import { useMemo, useCallback } from 'react';
import { useIsDemo } from '@/hooks/common/useIsDemo';
import { useSelectedOrgId } from '@/hooks/common/useSelectedOrgId';
import { filterByOrg } from '@/mockData/common/demoFilters';
import {
  ALL_CALENDAR_CONTACTS,
  ALL_CALENDAR_EVENTS,
  ALL_CALENDAR_TASKS,
} from '@/mockData';
import type { CalendarContact, CalendarEvent } from '@/types/calendar.types';

export function useCalendarData() {
  const isDemo = useIsDemo();
  const selectedOrgId = useSelectedOrgId();

  const events = useMemo(
    () => (isDemo ? filterByOrg(ALL_CALENDAR_EVENTS, selectedOrgId) : []),
    [isDemo, selectedOrgId],
  );
  const tasks = useMemo(
    () => (isDemo ? filterByOrg(ALL_CALENDAR_TASKS, selectedOrgId) : []),
    [isDemo, selectedOrgId],
  );
  const contacts = useMemo(
    () => (isDemo ? filterByOrg(ALL_CALENDAR_CONTACTS, selectedOrgId) : []),
    [isDemo, selectedOrgId],
  );

  const getContactById = useCallback(
    (id: string): CalendarContact | undefined =>
      isDemo ? contacts.find((contact) => contact.id === id) : undefined,
    [contacts, isDemo],
  );

  const getUpcomingEvents = useCallback(
    (limit?: number): CalendarEvent[] =>
      isDemo
        ? [...events]
            .sort((a, b) => {
              const dateA = new Date(`${a.date}T${a.startTime}`);
              const dateB = new Date(`${b.date}T${b.startTime}`);
              return dateA.getTime() - dateB.getTime();
            })
            .slice(0, limit ?? 5)
        : [],
    [events, isDemo],
  );

  return { events, tasks, contacts, getContactById, getUpcomingEvents };
}
